import { useState, useMemo, useEffect, useRef, useCallback } from "react";

const CARD_W = 172, CARD_H = 100, CARD_GAP = 60;
const LANE_LABEL_W = 120, LANE_PAD_Y = 20, LANE_PAD_X = 16;

const STAGE_FIELDS = new Set(["currentStage", "status", "stage"]);
const TEAM_FIELDS = new Set(["team", "org"]);
const ASSIGN_FIELDS = new Set(["assign", "assignee"]);

const SC = {
  Open:          { dot: "bg-blue-500",    bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-700" },
  Reopen:        { dot: "bg-purple-500",  bg: "bg-purple-50",  border: "border-purple-200",  text: "text-purple-700" },
  "In Progress": { dot: "bg-amber-500",   bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700" },
  Active:        { dot: "bg-amber-500",   bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700" },
  Complete:      { dot: "bg-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  Resolved:      { dot: "bg-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  Close:         { dot: "bg-gray-500",    bg: "bg-gray-50",    border: "border-gray-200",    text: "text-gray-700" },
  Closed:        { dot: "bg-gray-500",    bg: "bg-gray-50",    border: "border-gray-200",    text: "text-gray-700" },
  Reject:        { dot: "bg-red-500",     bg: "bg-red-50",     border: "border-red-200",     text: "text-red-700" },
  Draft:         { dot: "bg-gray-400",    bg: "bg-gray-50",    border: "border-gray-200",    text: "text-gray-600" },
  "Need More Info": { dot: "bg-orange-500", bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700" },
};
const DEFAULT_SC = { dot: "bg-gray-400", bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600" };
const stageColor = (s) => SC[s] || DEFAULT_SC;

export function formatDuration(ms) {
  if (!ms || ms <= 0) return "0m";
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function formatTimestamp(ts) {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "\u2014";
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${day} ${months[d.getMonth()]}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

function dwellColor(ms, maxMs) {
  const r = maxMs > 0 ? ms / maxMs : 0;
  if (r < 0.33) return "bg-green-500";
  if (r < 0.66) return "bg-amber-500";
  return "bg-red-500";
}

function shortName(email) {
  if (!email) return "Unknown";
  return email.split("@")[0].split(".").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function buildSwimLaneSteps(ticket) {
  if (!ticket) return [];
  const history = ticket.history || [];

  if (!history.length) {
    return [{
      stage: ticket.currentStage || "Open",
      team: ticket.team || "Default",
      user: ticket.assign || "Unassigned",
      timestamp: ticket.createdDate || ticket.created,
      changedBy: ticket.created || "System",
    }];
  }

  const sorted = [...history].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  let stage = null, team = null, user = null;
  for (const entry of sorted) {
    for (const c of (entry.changes || [])) {
      if (!stage && STAGE_FIELDS.has(c.field)) stage = c.oldValue;
      if (!team && TEAM_FIELDS.has(c.field)) team = c.oldValue;
      if (!user && ASSIGN_FIELDS.has(c.field)) user = c.oldValue;
    }
    if (stage && team && user) break;
  }

  stage = stage || ticket.currentStage || "Open";
  team = team || ticket.team || "Default";
  user = user || ticket.assign || "Unassigned";

  const steps = [{
    stage, team, user,
    timestamp: ticket.createdDate || ticket.created,
    changedBy: ticket.created || "System",
  }];

  for (const entry of sorted) {
    let changed = false;
    for (const c of (entry.changes || [])) {
      if (STAGE_FIELDS.has(c.field)) { stage = c.newValue; changed = true; }
      if (TEAM_FIELDS.has(c.field)) { team = c.newValue; changed = true; }
      if (ASSIGN_FIELDS.has(c.field)) { user = c.newValue; changed = true; }
    }
    if (changed) {
      steps.push({ stage, team, user, timestamp: entry.timestamp, changedBy: entry.changedBy });
    }
  }
  return steps;
}

export const SwimlaneDiagram = ({ ticket }) => {
  const steps = useMemo(() => buildSwimLaneSteps(ticket), [ticket]);
  const containerRef = useRef(null);
  const cardRefs = useRef(new Map());
  const [arrows, setArrows] = useState([]);

  const setCardRef = useCallback((idx) => (el) => {
    if (el) cardRefs.current.set(idx, el);
    else cardRefs.current.delete(idx);
  }, []);

  const { lanes, teamOrder } = useMemo(() => {
    const first = {};
    const map = {};
    steps.forEach((s, i) => {
      if (!(s.team in first)) first[s.team] = i;
      if (!map[s.team]) map[s.team] = [];
      map[s.team].push({ ...s, gi: i });
    });
    const order = Object.keys(first).sort((a, b) => first[a] - first[b]);
    return { lanes: map, teamOrder: order };
  }, [steps]);

  const dwells = useMemo(() =>
    steps.map((s, i) => i < steps.length - 1
      ? Math.max(0, new Date(steps[i + 1].timestamp) - new Date(s.timestamp))
      : 0
    ), [steps]);

  const maxDwell = useMemo(() => Math.max(...dwells, 1), [dwells]);

  const totalMs = useMemo(() =>
    steps.length > 1 ? new Date(steps[steps.length - 1].timestamp) - new Date(steps[0].timestamp) : 0
  , [steps]);

  const uniqueTeams = teamOrder.length;
  const maxCards = Math.max(...teamOrder.map(t => lanes[t].length), 1);
  const contentW = LANE_LABEL_W + LANE_PAD_X * 2 + maxCards * CARD_W + Math.max(0, maxCards - 1) * CARD_GAP + 32;

  useEffect(() => {
    const timer = setTimeout(() => {
      const ctr = containerRef.current;
      if (!ctr || steps.length < 2) { setArrows([]); return; }
      const cr = ctr.getBoundingClientRect();
      const arr = [];
      for (let i = 0; i < steps.length - 1; i++) {
        const fe = cardRefs.current.get(i);
        const te = cardRefs.current.get(i + 1);
        if (!fe || !te) continue;
        const fr = fe.getBoundingClientRect();
        const tr = te.getBoundingClientRect();
        arr.push({
          x1: fr.right - cr.left, y1: fr.top + fr.height / 2 - cr.top,
          x2: tr.left - cr.left, y2: tr.top + tr.height / 2 - cr.top,
          sameTeam: steps[i].team === steps[i + 1].team,
          userChanged: steps[i].user !== steps[i + 1].user,
          dwell: dwells[i],
        });
      }
      setArrows(arr);
    }, 80);
    return () => clearTimeout(timer);
  }, [steps, dwells, teamOrder]);

  if (!steps.length) return <p className="text-sm text-gray-400 text-center py-8">No lifecycle data available</p>;

  return (
    <div className="flex flex-col gap-3 text-xs select-none">
      {/* Summary Stats Bar */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-4 text-gray-600 font-semibold">
          <span>{steps.length} steps</span>
          <span className="text-gray-300">|</span>
          <span>{uniqueTeams} team{uniqueTeams !== 1 ? "s" : ""}</span>
          <span className="text-gray-300">|</span>
          <span>Total: {formatDuration(totalMs)}</span>
        </div>
        <div className="flex items-center gap-4 text-[10px] text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-0.5 bg-gray-400 rounded" /> transition
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-0.5 border-t-2 border-dashed border-amber-500" /> handoff
          </span>
        </div>
      </div>

      {/* Swimlane Diagram */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-x-auto">
        <div className="relative" ref={containerRef} style={{ minWidth: contentW }}>
          {teamOrder.map((team, li) => (
            <div
              key={team}
              className={`flex items-stretch ${li % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
              style={{ minHeight: CARD_H + LANE_PAD_Y * 2 }}
            >
              <div
                className="flex flex-col items-center justify-center border-r border-gray-200 shrink-0 px-2 text-center"
                style={{ width: LANE_LABEL_W }}
              >
                <span className="font-bold text-gray-700 text-[11px] leading-tight break-words">
                  {shortName(team)}
                </span>
                <span className="text-[10px] text-gray-400 mt-0.5">
                  {lanes[team].length} step{lanes[team].length !== 1 ? "s" : ""}
                </span>
              </div>

              <div
                className="flex items-center"
                style={{ gap: CARD_GAP, padding: `${LANE_PAD_Y}px ${LANE_PAD_X}px` }}
              >
                {lanes[team].map((step) => {
                  const isLast = step.gi === steps.length - 1;
                  const sc = stageColor(step.stage);
                  const dw = dwells[step.gi];
                  const dwPct = maxDwell > 0 ? Math.max(4, (dw / maxDwell) * 100) : 4;
                  const dc = dwellColor(dw, maxDwell);

                  return (
                    <div key={step.gi} className="relative" style={{ width: CARD_W, flexShrink: 0 }}>
                      {isLast && (
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase tracking-wider bg-jiojbm50 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                          Current
                        </div>
                      )}
                      <div
                        ref={setCardRef(step.gi)}
                        className={`rounded-xl border-2 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between p-2.5 overflow-hidden ${
                          isLast ? "border-jiojbm50 ring-2 ring-jiojbmpurple/20" : "border-gray-200"
                        }`}
                        style={{ width: CARD_W, height: CARD_H }}
                      >
                        <div className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 w-fit border ${sc.bg} ${sc.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          <span className={`text-[10px] font-bold ${sc.text} truncate max-w-[120px]`}>{step.stage}</span>
                        </div>

                        <div className="flex items-center gap-1 text-[10px] text-gray-700 truncate">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 text-gray-400">
                            <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z" />
                          </svg>
                          <span className="truncate font-medium">{shortName(step.user)}</span>
                        </div>

                        {step.changedBy && step.changedBy !== step.user && (
                          <div className="flex items-center gap-1 text-[9px] text-gray-400 italic truncate -mt-0.5">
                            <span>&rarr;</span>
                            <span className="truncate">by {shortName(step.changedBy)}</span>
                          </div>
                        )}

                        <div className="text-[9px] text-gray-400">{formatTimestamp(step.timestamp)}</div>

                        {step.gi < steps.length - 1 && (
                          <div className="flex items-center gap-1 -mb-0.5">
                            <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${dc}`} style={{ width: `${dwPct}%` }} />
                            </div>
                            <span className="text-[8px] text-gray-400 whitespace-nowrap">{formatDuration(dw)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 2 }}>
            <defs>
              <marker id="sl-arrow-gray" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <path d="M0,0 L8,3 L0,6" fill="#9ca3af" />
              </marker>
              <marker id="sl-arrow-amber" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <path d="M0,0 L8,3 L0,6" fill="#f59e0b" />
              </marker>
            </defs>
            {arrows.map((a, i) => {
              const sameY = Math.abs(a.y1 - a.y2) < 4;
              const pathD = sameY
                ? `M${a.x1},${a.y1} L${a.x2},${a.y2}`
                : `M${a.x1},${a.y1} C${(a.x1 + a.x2) / 2},${a.y1} ${(a.x1 + a.x2) / 2},${a.y2} ${a.x2},${a.y2}`;

              return (
                <path
                  key={i}
                  d={pathD}
                  fill="none"
                  stroke={a.sameTeam ? "#9ca3af" : "#f59e0b"}
                  strokeWidth={a.sameTeam ? 2 : 2.5}
                  strokeDasharray={a.sameTeam ? undefined : "6 4"}
                  markerEnd={a.sameTeam ? "url(#sl-arrow-gray)" : "url(#sl-arrow-amber)"}
                />
              );
            })}
          </svg>

          {arrows.map((a, i) => {
            const mx = (a.x1 + a.x2) / 2;
            const my = (a.y1 + a.y2) / 2;
            return (
              <div key={`lbl${i}`} className="absolute pointer-events-none flex flex-col items-center gap-0.5" style={{ left: mx, top: my, transform: "translate(-50%, -50%)", zIndex: 3 }}>
                {!a.sameTeam && (
                  <span className="text-[8px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0 leading-tight">
                    Handoff
                  </span>
                )}
                {a.sameTeam && a.userChanged && (
                  <span className="w-4 h-4 rounded-full bg-white border border-gray-300 flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="#6b7280">
                      <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z" />
                    </svg>
                  </span>
                )}
                <span className="text-[8px] font-bold text-white bg-jiojbm50 rounded-full px-1.5 py-0 leading-tight whitespace-nowrap">
                  {formatDuration(a.dwell)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Journey Timeline Strip */}
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 flex items-center gap-1 overflow-x-auto">
        <span className="text-[9px] font-black uppercase tracking-wider text-gray-400 mr-2 shrink-0">Journey</span>
        {steps.map((step, i) => {
          const sc = stageColor(step.stage);
          return (
            <div key={i} className="flex items-center gap-1 shrink-0">
              {i > 0 && (
                <svg width="12" height="10" viewBox="0 0 12 10" className="text-gray-300 shrink-0">
                  <path d="M1,5 L9,5 M7,2 L10,5 L7,8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              )}
              <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${sc.bg} ${sc.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                <span className={`text-[9px] font-bold ${sc.text}`}>{step.stage}</span>
                <span className="text-[8px] text-gray-400">{shortName(step.user)}</span>
                {i < steps.length - 1 && (
                  <span className="text-[8px] text-gray-400">{formatDuration(dwells[i])}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
